import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import QRCode from 'qrcode.react';

const { FiSettings, FiEye, FiQrCode, FiLogOut, FiSave, FiUpload, FiMove, FiMaximize, FiMinimize, FiType, FiMapPin, FiLayers, FiCheck, FiRefreshCw, FiDatabase, FiWifi, FiWifiOff } = FiIcons;

const CityDashboard = () => {
  const { user, logout, dbOperations, isConnectedToSupabase } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('basic');
  const [config, setConfig] = useState({
    // Basic settings
    primaryColor: '#3B82F6',
    secondaryColor: '#EF4444',
    textColor: '#FFFFFF',
    customText: '',
    showLocation: true,
    showGreeting: true,
    showAdditionalText: false,
    greetingText: 'Viele Grüße aus',
    additionalText: '',
    textBackground: true,
    locationBackground: true,
    additionalTextBackground: true,
    
    // Logo settings
    logo: '',
    logoPosition: { x: '2%', y: '2%' },
    logoSize: '20%',
    logoOpacity: 1,
    
    // Second image settings
    secondImage: '',
    secondImagePosition: { x: 'right', y: '2%' },
    secondImageSize: '20%',
    secondImageOpacity: 1,
    
    // Text overlay settings
    textPosition: { x: 'left', y: 'middle' },
    textSize: '1.8vmin',
    textOpacity: 0.9,
    textStyle: 'bold',
    fontFamily: 'Arial',
    
    // Location overlay settings
    locationPosition: { x: 'center', y: 'bottom' },
    locationSize: '2.2vmin',
    locationOpacity: 1,
    locationFontFamily: 'Arial',
    locationColor: '#FFFFFF',
    locationStyle: 'bold',
    
    // Additional text settings
    additionalTextPosition: { x: 'center', y: 'bottom' },
    additionalTextSize: '1.6vmin',
    additionalTextOpacity: 0.9,
    additionalFontFamily: 'Arial',
    additionalTextColor: '#FFFFFF',
    additionalTextStyle: 'normal',
  });
  
  const [previewMode, setPreviewMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [configTimestamp, setConfigTimestamp] = useState(null);
  const [saveStatus, setSaveStatus] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    setLoading(false);
  }, [user, navigate]);

  useEffect(() => {
    // Load saved config from database or localStorage
    if (user?.id) {
      loadConfiguration();
    }
  }, [user]);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      const savedConfig = await dbOperations.getConfig(user.id);
      
      if (savedConfig) {
        // Store timestamp if available
        if (savedConfig._timestamp) {
          setConfigTimestamp(savedConfig._timestamp);
        }
        
        // Convert any absolute pixel positions to relative percentages if they're not already
        setConfig(convertToRelativeUnits(savedConfig));
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert absolute pixel positions to relative percentages
  const convertToRelativeUnits = (configObj) => {
    if (!configObj) return config;
    const converted = { ...configObj };

    // Handle logo position
    if (typeof converted.logoPosition?.x === 'number') {
      converted.logoPosition = {
        ...converted.logoPosition,
        x: `${converted.logoPosition.x / 10}%`
      };
    }
    if (typeof converted.logoPosition?.y === 'number') {
      converted.logoPosition = {
        ...converted.logoPosition,
        y: `${converted.logoPosition.y / 10}%`
      };
    }

    // Handle second image position
    if (typeof converted.secondImagePosition?.x === 'number' && !['left', 'center', 'right'].includes(converted.secondImagePosition.x)) {
      converted.secondImagePosition = {
        ...converted.secondImagePosition,
        x: `${converted.secondImagePosition.x / 10}%`
      };
    }
    if (typeof converted.secondImagePosition?.y === 'number') {
      converted.secondImagePosition = {
        ...converted.secondImagePosition,
        y: `${converted.secondImagePosition.y / 10}%`
      };
    }

    // Convert logo and second image size to percentage of container
    if (typeof converted.logoSize === 'number') {
      converted.logoSize = `${converted.logoSize / 4}%`;
    }
    if (typeof converted.secondImageSize === 'number') {
      converted.secondImageSize = `${converted.secondImageSize / 4}%`;
    }

    // Convert text sizes to relative viewport units
    if (typeof converted.textSize === 'number') {
      converted.textSize = `${converted.textSize / 10}vmin`;
    }
    if (typeof converted.locationSize === 'number') {
      converted.locationSize = `${converted.locationSize / 10}vmin`;
    }
    if (typeof converted.additionalTextSize === 'number') {
      converted.additionalTextSize = `${converted.additionalTextSize / 10}vmin`;
    }

    // Add missing properties with defaults
    if (!converted.textColor) {
      converted.textColor = '#FFFFFF';
    }
    if (!converted.fontFamily) {
      converted.fontFamily = 'Arial';
    }
    if (!converted.locationFontFamily) {
      converted.locationFontFamily = 'Arial';
    }
    if (!converted.additionalFontFamily) {
      converted.additionalFontFamily = 'Arial';
    }
    if (converted.textBackground === undefined) {
      converted.textBackground = true;
    }
    if (converted.locationBackground === undefined) {
      converted.locationBackground = true;
    }
    if (converted.additionalTextBackground === undefined) {
      converted.additionalTextBackground = true;
    }
    if (!converted.locationStyle) {
      converted.locationStyle = 'bold';
    }
    if (!converted.additionalTextStyle) {
      converted.additionalTextStyle = 'normal';
    }
    if (!converted.textPosition) {
      converted.textPosition = { x: 'left', y: 'middle' };
    }

    return converted;
  };

  const handleSaveConfig = async () => {
    if (!user?.id) return;
    
    try {
      setSaveStatus('saving');
      
      // Add timestamp to config
      const configWithTimestamp = {
        ...config,
        _timestamp: Date.now()
      };
      
      // Save to database or localStorage
      await dbOperations.saveConfig(user.id, configWithTimestamp);
      setConfigTimestamp(configWithTimestamp._timestamp);
      
      // Show success status
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (error) {
      console.error('Error saving configuration:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  // Function to clear cache and reload app
  const clearCacheAndReload = () => {
    // Force a refresh with cache clearing parameters
    const timestamp = new Date().getTime();
    window.location.href = `${window.location.origin}/#/city/${user.id}?_t=${timestamp}&forceClear=true`;
  };

  const handleFileUpload = (type, file) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setConfig(prev => ({ ...prev, [type]: e.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const updatePosition = (element, axis, value) => {
    setConfig(prev => ({
      ...prev,
      [`${element}Position`]: { ...prev[`${element}Position`], [axis]: value }
    }));
  };

  const cityUrl = `${window.location.origin}/#/city/${user?.id}`;

  // Position options for dropdowns
  const horizontalPositions = [
    { value: 'left', label: 'Links' },
    { value: 'center', label: 'Mitte' },
    { value: 'right', label: 'Rechts' }
  ];

  const verticalPositions = [
    { value: 'top', label: 'Oben' },
    { value: 'middle', label: 'Mitte' },
    { value: 'bottom', label: 'Unten' }
  ];

  // Font family options
  const fontFamilies = [
    { value: 'Arial', label: 'Arial' },
    { value: 'Verdana', label: 'Verdana' },
    { value: 'Helvetica', label: 'Helvetica' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Courier New', label: 'Courier New' },
    { value: 'Tahoma', label: 'Tahoma' },
    { value: 'Trebuchet MS', label: 'Trebuchet MS' }
  ];

  // Font style options
  const fontStyles = [
    { value: 'normal', label: 'Normal' },
    { value: 'bold', label: 'Fett' },
    { value: 'italic', label: 'Kursiv' },
    { value: 'bolditalic', label: 'Fett & Kursiv' }
  ];

  // Color options
  const colorOptions = [
    { value: '#FFFFFF', label: 'Weiß', className: 'bg-white border border-gray-300' },
    { value: '#000000', label: 'Schwarz', className: 'bg-black text-white' },
    { value: 'primary', label: 'Primärfarbe', dynamic: true },
    { value: 'secondary', label: 'Sekundärfarbe', dynamic: true }
  ];

  const getColorValue = (colorOption) => {
    if (colorOption === 'primary') return config?.primaryColor || '#3B82F6';
    if (colorOption === 'secondary') return config?.secondaryColor || '#EF4444';
    return colorOption;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <p className="text-xl text-gray-800 mb-4">Sie sind nicht angemeldet.</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Zurück zur Anmeldung
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                CityShot Dashboard
              </h1>
              <p className="text-gray-600">Willkommen, {user.cityName}</p>
              
              {/* Database connection status */}
              <div className="flex items-center gap-2 mt-1">
                <SafeIcon 
                  icon={isConnectedToSupabase ? FiDatabase : FiWifiOff} 
                  className={`text-sm ${isConnectedToSupabase ? 'text-green-600' : 'text-orange-600'}`}
                />
                <span className={`text-xs ${isConnectedToSupabase ? 'text-green-600' : 'text-orange-600'}`}>
                  {isConnectedToSupabase ? 'Supabase verbunden' : 'Lokaler Modus'}
                </span>
              </div>
              
              {configTimestamp && (
                <p className="text-xs text-gray-500">
                  Letzte Aktualisierung: {new Date(configTimestamp).toLocaleString()}
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setPreviewMode(!previewMode)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiEye} />
                Vorschau
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <SafeIcon icon={FiLogOut} />
                Abmelden
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Configuration Panel */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiSettings} className="text-2xl text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                App-Konfiguration
              </h2>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b mb-6">
              <button
                className={`px-4 py-2 font-medium ${activeTab === 'basic' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('basic')}
              >
                Basis-Einstellungen
              </button>
              <button
                className={`px-4 py-2 font-medium ${activeTab === 'overlays' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setActiveTab('overlays')}
              >
                Overlays & Positionen
              </button>
            </div>

            {activeTab === 'basic' && (
              <div className="space-y-6">
                {/* Colors */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Primärfarbe
                    </label>
                    <input
                      type="color"
                      value={config.primaryColor}
                      onChange={(e) => setConfig(prev => ({ ...prev, primaryColor: e.target.value }))}
                      className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sekundärfarbe
                    </label>
                    <input
                      type="color"
                      value={config.secondaryColor}
                      onChange={(e) => setConfig(prev => ({ ...prev, secondaryColor: e.target.value }))}
                      className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Textfarbe
                    </label>
                    <input
                      type="color"
                      value={config.textColor || '#FFFFFF'}
                      onChange={(e) => setConfig(prev => ({ ...prev, textColor: e.target.value }))}
                      className="w-full h-12 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>

                {/* Font Family */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Schriftart
                  </label>
                  <select
                    value={config.fontFamily || 'Arial'}
                    onChange={(e) => setConfig(prev => ({ ...prev, fontFamily: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {fontFamilies.map(font => (
                      <option key={font.value} value={font.value}>{font.label}</option>
                    ))}
                  </select>
                </div>

                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Stadt-Logo
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('logo', e.target.files[0])}
                      className="hidden"
                      id="logo-upload"
                    />
                    <label
                      htmlFor="logo-upload"
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <SafeIcon icon={FiUpload} />
                      Logo hochladen
                    </label>
                    {config.logo && (
                      <img src={config.logo} alt="Logo" className="h-12 w-12 object-contain rounded" />
                    )}
                  </div>
                </div>

                {/* Second Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Zusätzliches Bild (optional)
                  </label>
                  <div className="flex items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileUpload('secondImage', e.target.files[0])}
                      className="hidden"
                      id="second-image-upload"
                    />
                    <label
                      htmlFor="second-image-upload"
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50"
                    >
                      <SafeIcon icon={FiUpload} />
                      Bild hochladen
                    </label>
                    {config.secondImage && (
                      <img src={config.secondImage} alt="Second" className="h-12 w-12 object-contain rounded" />
                    )}
                  </div>
                </div>

                {/* Custom Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Benutzerdefinierter Text
                  </label>
                  <input
                    type="text"
                    value={config.customText}
                    onChange={(e) => setConfig(prev => ({ ...prev, customText: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="z.B. Besuche unsere schöne Stadt!"
                  />
                </div>

                {/* Location Settings */}
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showLocation"
                        checked={config.showLocation}
                        onChange={(e) => setConfig(prev => ({ ...prev, showLocation: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="showLocation" className="ml-2 text-sm text-gray-700">
                        GPS-Standort anzeigen
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showGreeting"
                        checked={config.showGreeting}
                        onChange={(e) => setConfig(prev => ({ ...prev, showGreeting: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="showGreeting" className="ml-2 text-sm text-gray-700">
                        Grußtext anzeigen
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="showAdditionalText"
                        checked={config.showAdditionalText}
                        onChange={(e) => setConfig(prev => ({ ...prev, showAdditionalText: e.target.checked }))}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="showAdditionalText" className="ml-2 text-sm text-gray-700">
                        Zusatztext anzeigen
                      </label>
                    </div>
                  </div>

                  {config.showGreeting && (
                    <input
                      type="text"
                      value={config.greetingText}
                      onChange={(e) => setConfig(prev => ({ ...prev, greetingText: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Viele Grüße aus"
                    />
                  )}

                  {config.showAdditionalText && (
                    <input
                      type="text"
                      value={config.additionalText}
                      onChange={(e) => setConfig(prev => ({ ...prev, additionalText: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="z.B. Schloss Neuschwanstein"
                    />
                  )}
                </div>

                {/* Text Style Settings */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text-Stil
                    </label>
                    <select
                      value={config.textStyle}
                      onChange={(e) => setConfig(prev => ({ ...prev, textStyle: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {fontStyles.map(style => (
                        <option key={style.value} value={style.value}>{style.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Text-Transparenz
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={config.textOpacity}
                      onChange={(e) => setConfig(prev => ({ ...prev, textOpacity: parseFloat(e.target.value) }))}
                      className="w-full"
                    />
                    <span className="text-sm text-gray-500">{Math.round(config.textOpacity * 100)}%</span>
                  </div>
                </div>

                {/* Background Settings */}
                <div className="grid grid-cols-3 gap-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="textBackground"
                      checked={config.textBackground}
                      onChange={(e) => setConfig(prev => ({ ...prev, textBackground: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="textBackground" className="ml-2 text-sm text-gray-700">
                      Text-Hintergrund
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="locationBackground"
                      checked={config.locationBackground}
                      onChange={(e) => setConfig(prev => ({ ...prev, locationBackground: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="locationBackground" className="ml-2 text-sm text-gray-700">
                      Standort-Hintergrund
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="additionalTextBackground"
                      checked={config.additionalTextBackground}
                      onChange={(e) => setConfig(prev => ({ ...prev, additionalTextBackground: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="additionalTextBackground" className="ml-2 text-sm text-gray-700">
                      Zusatztext-Hintergrund
                    </label>
                  </div>
                </div>

                {/* QR Code */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <SafeIcon icon={FiQrCode} className="text-xl text-blue-600" />
                    <h3 className="font-medium text-gray-900">QR-Code für App-URL</h3>
                  </div>
                  <div className="flex justify-center bg-white p-4 rounded border border-gray-200">
                    <QRCode
                      value={cityUrl}
                      size={128}
                      bgColor={"#ffffff"}
                      fgColor={"#000000"}
                      level={"M"}
                      includeMargin={true}
                      renderAs={"svg"}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Scannen Sie diesen QR-Code, um die App zu öffnen
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'overlays' && (
              <div className="space-y-6">
                <div className="bg-blue-50 p-3 rounded-lg mb-6">
                  <div className="flex items-center gap-2 text-blue-700">
                    <SafeIcon icon={FiLayers} className="text-xl" />
                    <p className="text-sm">
                      Hier können Sie die Position, Größe und Transparenz aller Overlays anpassen.
                    </p>
                  </div>
                </div>

                {/* Logo Position & Size */}
                {config.logo && (
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <SafeIcon icon={FiMove} className="text-blue-600" />
                      <h3 className="font-medium">Logo-Einstellungen</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Horizontale Position (%)
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={parseInt(config.logoPosition.x) || 2}
                          onChange={(e) => updatePosition('logo', 'x', `${e.target.value}%`)}
                          className="w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {config.logoPosition.x}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Vertikale Position (%)
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={parseInt(config.logoPosition.y) || 2}
                          onChange={(e) => updatePosition('logo', 'y', `${e.target.value}%`)}
                          className="w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {config.logoPosition.y}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Größe (%)
                        </label>
                        <input
                          type="range"
                          min="5"
                          max="50"
                          value={parseInt(config.logoSize) || 20}
                          onChange={(e) => setConfig(prev => ({ ...prev, logoSize: `${e.target.value}%` }))}
                          className="w-full"
                        />
                        <div className="text-xs text-gray-500 mt-1">
                          {config.logoSize}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Transparenz: {Math.round(config.logoOpacity * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={config.logoOpacity}
                        onChange={(e) => setConfig(prev => ({ ...prev, logoOpacity: parseFloat(e.target.value) }))}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}

                {/* Additional overlay configurations would go here */}
                {/* ... (continuing with the same pattern for other elements) ... */}
              </div>
            )}

            {/* Save Button and Status */}
            <div className="mt-6 space-y-4">
              <button
                onClick={handleSaveConfig}
                disabled={saveStatus === 'saving'}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-70"
              >
                {saveStatus === 'saving' ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Speichere...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSave} />
                    Konfiguration speichern
                  </>
                )}
              </button>
              
              {/* Save status message */}
              {saveStatus === 'success' && (
                <div className="bg-green-100 text-green-800 p-3 rounded-lg text-sm flex items-center">
                  <SafeIcon icon={FiCheck} className="mr-2" />
                  Konfiguration erfolgreich gespeichert!
                </div>
              )}
              
              {saveStatus === 'error' && (
                <div className="bg-red-100 text-red-800 p-3 rounded-lg text-sm flex items-center">
                  <SafeIcon icon={FiRefreshCw} className="mr-2" />
                  Fehler beim Speichern. Bitte versuchen Sie es erneut.
                </div>
              )}
              
              {/* Cache clearing button */}
              <button
                onClick={clearCacheAndReload}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <SafeIcon icon={FiRefreshCw} />
                Cache leeren und App neu laden
              </button>
              
              <p className="text-xs text-gray-500 text-center">
                Sollten Ihre Änderungen nicht in der App erscheinen, klicken Sie auf "Cache leeren".
              </p>
            </div>
          </motion.div>

          {/* Preview & QR Code */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* QR Code & URL */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <SafeIcon icon={FiQrCode} className="text-2xl text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">
                  Ihre Selfie-App
                </h2>
              </div>
              <div className="text-center space-y-4">
                <div className="bg-gray-100 p-4 rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">App-URL:</p>
                  <p className="text-blue-600 font-mono text-sm break-all">{cityUrl}</p>
                </div>
                <div className="bg-white border-2 border-gray-200 p-6 rounded-lg inline-block">
                  <QRCode
                    value={cityUrl}
                    size={128}
                    bgColor={"#ffffff"}
                    fgColor={"#000000"}
                    level={"L"}
                    includeMargin={false}
                    renderAs={"svg"}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Teilen Sie diese URL oder den QR-Code mit Ihren Besuchern
                </p>
                
                {/* App open button */}
                <a 
                  href={cityUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <SafeIcon icon={FiEye} className="inline-block mr-2" />
                  App öffnen
                </a>
              </div>
            </div>

            {/* Preview */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vorschau</h3>
              <div className="bg-gray-800 rounded-lg p-4 aspect-[4/3] max-w-md mx-auto relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
                  <span className="text-gray-400">Kamera-Vorschau</span>
                </div>

                {/* Overlay Preview */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Logo with relative position and size */}
                  {config.logo && (
                    <div
                      style={{
                        position: 'absolute',
                        left: typeof config.logoPosition.x === 'string' ? config.logoPosition.x : `${config.logoPosition.x}px`,
                        top: typeof config.logoPosition.y === 'string' ? config.logoPosition.y : `${config.logoPosition.y}px`,
                        opacity: config.logoOpacity,
                        height: typeof config.logoSize === 'string' ? config.logoSize : `${config.logoSize}px`,
                        width: 'auto'
                      }}
                    >
                      <img src={config.logo} alt="Logo" className="h-full w-auto object-contain" />
                    </div>
                  )}

                  {/* Custom Text */}
                  {config.customText && (
                    <div
                      style={{
                        position: 'absolute',
                        ...(config.textPosition.x === 'left'
                          ? { left: '2%', textAlign: 'left' }
                          : config.textPosition.x === 'center'
                          ? { left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }
                          : config.textPosition.x === 'right'
                          ? { right: '2%', textAlign: 'right' }
                          : {
                              left: typeof config.textPosition.x === 'string'
                                ? config.textPosition.x
                                : `${config.textPosition.x}px`
                            }),
                        ...(config.textPosition.y === 'top'
                          ? { top: '2%' }
                          : config.textPosition.y === 'middle'
                          ? {
                              top: '50%',
                              transform: `${
                                config.textPosition.x === 'center'
                                  ? 'translate(-50%, -50%)'
                                  : 'translateY(-50%)'
                              }`
                            }
                          : config.textPosition.y === 'bottom'
                          ? { bottom: '2%' }
                          : {
                              top: typeof config.textPosition.y === 'string'
                                ? config.textPosition.y
                                : `${config.textPosition.y}px`
                            }),
                        opacity: config.textOpacity,
                        fontSize: config.textSize,
                        fontWeight: config.textStyle.includes('bold') ? 'bold' : 'normal',
                        fontStyle: config.textStyle.includes('italic') ? 'italic' : 'normal',
                        fontFamily: config.fontFamily || 'Arial',
                        padding: '10px',
                        borderRadius: '8px',
                        backgroundColor: config.textBackground ? 'rgba(0,0,0,0.5)' : 'transparent',
                        color: getColorValue(config.textColor) || 'white',
                        maxWidth: '80%'
                      }}
                    >
                      {config.customText}
                    </div>
                  )}

                  {/* Location with Greeting */}
                  {config.showLocation && (
                    <div
                      style={{
                        position: 'absolute',
                        ...(config.locationPosition.x === 'left'
                          ? { left: '2%', textAlign: 'left' }
                          : config.locationPosition.x === 'center'
                          ? { left: '50%', transform: 'translateX(-50%)', textAlign: 'center' }
                          : config.locationPosition.x === 'right'
                          ? { right: '2%', textAlign: 'right' }
                          : {
                              left: typeof config.locationPosition.x === 'string'
                                ? config.locationPosition.x
                                : `${config.locationPosition.x}px`
                            }),
                        ...(config.locationPosition.y === 'top'
                          ? { top: '2%' }
                          : config.locationPosition.y === 'middle'
                          ? {
                              top: '50%',
                              transform: `${
                                config.locationPosition.x === 'center'
                                  ? 'translate(-50%, -50%)'
                                  : 'translateY(-50%)'
                              }`
                            }
                          : config.locationPosition.y === 'bottom'
                          ? { bottom: '2%' }
                          : {
                              top: typeof config.locationPosition.y === 'string'
                                ? config.locationPosition.y
                                : `${config.locationPosition.y}px`
                            }),
                        opacity: config.locationOpacity,
                        padding: '10px',
                        borderRadius: '8px',
                        backgroundColor: config.locationBackground ? 'rgba(0,0,0,0.5)' : 'transparent',
                        color: getColorValue(config.locationColor) || 'white',
                        fontFamily: config.locationFontFamily || 'Arial',
                      }}
                    >
                      {config.showGreeting && config.greetingText && (
                        <div
                          style={{
                            fontSize:
                              typeof config.locationSize === 'string'
                                ? `calc(${config.locationSize} * 0.8)`
                                : Math.max((config.locationSize || 22) * 0.8, 12) + 'px',
                            fontWeight: config.locationStyle?.includes('bold') ? 'bold' : 'normal',
                            fontStyle: config.locationStyle?.includes('italic') ? 'italic' : 'normal'
                          }}
                        >
                          {config.greetingText}
                        </div>
                      )}
                      <div
                        className="flex items-center justify-center gap-2"
                        style={{
                          fontSize: config.locationSize,
                          fontWeight: config.locationStyle?.includes('bold') ? 'bold' : 'normal',
                          fontStyle: config.locationStyle?.includes('italic') ? 'italic' : 'normal'
                        }}
                      >
                        <SafeIcon icon={FiMapPin} />
                        {user.cityName}
                      </div>
                      {/* Additional Text */}
                      {config.showAdditionalText && config.additionalText && (
                        <div
                          style={{
                            fontSize: config.additionalTextSize,
                            marginTop: '4px',
                            opacity: config.additionalTextOpacity,
                            fontFamily: config.additionalFontFamily || 'Arial',
                            color: getColorValue(config.additionalTextColor) || 'white',
                            fontWeight: config.additionalTextStyle?.includes('bold') ? 'bold' : 'normal',
                            fontStyle: config.additionalTextStyle?.includes('italic') ? 'italic' : 'normal',
                            backgroundColor: config.additionalTextBackground ? 'rgba(0,0,0,0.3)' : 'transparent',
                            padding: config.additionalTextBackground ? '4px 8px' : '0',
                            borderRadius: config.additionalTextBackground ? '4px' : '0'
                          }}
                        >
                          {config.additionalText}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default CityDashboard;